'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFloatingCardContext, FloatingCardContextProvider } from '@components/floating/FloatingCardContextProvider';
import { IconThumbtack } from '@endurancewarranty/icons';
import { cx, Card, Flex, Text, useEventListener, useOnClickOutside } from '@endurancewarranty/ui';
import { clampFloatingPosition, useDraggable } from '@hooks/useDraggable';
import type { FC, KeyboardEvent, ReactNode, RefObject } from 'react';
export type TFloatingCardPosition = Readonly<{
  x: number;
  y: number;
}>;
export type TFloatingCardTriggerState = Readonly<{
  isOpen: boolean;
  isPinned: boolean;
}>;
type TFloatingCardInitialPositionArgs = Readonly<{
  panelWidth: number;
  panelPadding: number;
}>;
type TFloatingCardComponent = FC<TFloatingCardProps> & {
  Header: FC<TFloatingCardHeaderProps>;
  Content: FC<TFloatingCardContentProps>;
};
type TFloatingCardProps = Readonly<{
  children: ReactNode;
  trigger?: ReactNode | ((state: TFloatingCardTriggerState) => ReactNode);
  edgeThreshold?: number;
  initialPosition?: TFloatingCardPosition | ((args: TFloatingCardInitialPositionArgs) => TFloatingCardPosition);
  panelPadding?: number;
  panelWidth?: number;
  triggerAriaLabel?: string;
  triggerClassName?: string;
  zIndex?: number;
  openOnMount?: boolean;
  pinOnMount?: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}>;
type TFloatingCardHeaderProps = Readonly<{
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  subtitle?: ReactNode;
  title?: ReactNode;
}>;
type TFloatingCardContentProps = Readonly<{
  children: ReactNode;
  className?: string;
}>;
const DEFAULT_EDGE_THRESHOLD = 40;
const DEFAULT_NAVBAR_HEIGHT = 64;
const DEFAULT_PANEL_PADDING = 12;
const DEFAULT_PANEL_WIDTH = 480;
const DEFAULT_Z_INDEX = 50;
const getDefaultInitialPosition = ({
  panelWidth,
  panelPadding
}: TFloatingCardInitialPositionArgs): TFloatingCardPosition => ({
  x: window.innerWidth - panelWidth - panelPadding,
  y: DEFAULT_NAVBAR_HEIGHT + panelPadding
});
const FloatingCardHeader: FC<TFloatingCardHeaderProps> = t0 => {
  const $ = _c(29);
  const {
    actions,
    children,
    className,
    subtitle,
    title
  } = t0;
  const {
    handleDragStart,
    isDragging,
    isPinned,
    togglePinned
  } = useFloatingCardContext();
  const t1 = isDragging ? "cursor-grabbing" : "cursor-grab";
  let t2;
  if ($[0] !== className || $[1] !== t1) {
    t2 = cx("flex select-none flex-row items-start justify-between px-3 py-2", t1, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== title) {
    t3 = title != null && <Text.Body size="xs" fontWeight="bold">{title}</Text.Body>;
    $[3] = title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== subtitle) {
    t4 = subtitle != null && <Flex direction="row" items="center" className="gap-1">{subtitle}</Flex>;
    $[5] = subtitle;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children || $[8] !== t3 || $[9] !== t4) {
    t5 = <Flex direction="col">{t3}{t4}{children}</Flex>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = isPinned ? "Unpin panel" : "Pin panel";
  const t7 = isPinned ? "Unpin panel" : "Pin panel";
  const t8 = isPinned ? "currentColor" : "none";
  const t9 = isPinned ? 1 : 0.4;
  let t10;
  if ($[11] !== t9) {
    t10 = {
      opacity: t9
    };
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t8) {
    t11 = <IconThumbtack className="h-5 w-5" fill={t8} style={t10} />;
    $[13] = t10;
    $[14] = t8;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t6 || $[18] !== t7 || $[19] !== togglePinned) {
    t12 = <button type="button" aria-label={t6} className="appearance-none border-0 bg-transparent p-0 text-current hover:cursor-pointer hover:opacity-70" title={t7} onClick={togglePinned}>{t11}</button>;
    $[16] = t11;
    $[17] = t6;
    $[18] = t7;
    $[19] = togglePinned;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== actions || $[22] !== t12) {
    t13 = <Flex direction="row" items="center" className="gap-2" data-floating-card-no-drag={true}>{actions}{t12}</Flex>;
    $[21] = actions;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== handleDragStart || $[25] !== t13 || $[26] !== t2 || $[27] !== t5) {
    t14 = <div className={t2} onPointerDown={handleDragStart}>{t5}{t13}</div>;
    $[24] = handleDragStart;
    $[25] = t13;
    $[26] = t2;
    $[27] = t5;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
};
const FloatingCardContent: FC<TFloatingCardContentProps> = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("px-4 pb-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const FloatingCard: TFloatingCardComponent = ({
  children,
  trigger,
  edgeThreshold = DEFAULT_EDGE_THRESHOLD,
  initialPosition = getDefaultInitialPosition,
  panelPadding = DEFAULT_PANEL_PADDING,
  panelWidth = DEFAULT_PANEL_WIDTH,
  triggerAriaLabel = 'Toggle floating panel',
  triggerClassName,
  zIndex = DEFAULT_Z_INDEX,
  openOnMount = false,
  pinOnMount: pinnedOnMount = false,
  isOpen: controlledIsOpen,
  onOpenChange
}) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(openOnMount);
  const isControlled = controlledIsOpen != null;
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;
  const setIsOpen = (updater: boolean | ((current: boolean) => boolean)): void => {
    const nextIsOpen = typeof updater === 'function' ? updater(isOpen) : updater;
    if (isControlled) {
      onOpenChange?.(nextIsOpen);
      return;
    }
    setUncontrolledIsOpen(nextIsOpen);
  };
  const [isPinned, setIsPinned] = useState(pinnedOnMount);
  const toggleRef = useRef<Nullable<HTMLDivElement>>(null);
  const panelRef = useRef<Nullable<HTMLDivElement>>(null);
  const {
    position,
    setPosition,
    isDragging,
    handleDragStart
  } = useDraggable({
    panelWidth,
    edgeThreshold,
    panelRef
  });
  const getClampedPosition = (nextPosition: TFloatingCardPosition): TFloatingCardPosition => {
    const rect = panelRef.current?.getBoundingClientRect();
    return clampFloatingPosition(nextPosition, {
      panelWidth: rect?.width ?? panelWidth,
      panelHeight: rect?.height ?? edgeThreshold,
      edgeThreshold,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight
    });
  };
  const getInitialPosition = (): TFloatingCardPosition => getClampedPosition(typeof initialPosition === 'function' ? initialPosition({
    panelWidth,
    panelPadding
  }) : initialPosition);
  const togglePinned = (): void => {
    setIsPinned(current => !current);
  };
  const handleOpen = (): void => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    if (position === null) {
      setPosition(getInitialPosition());
    }
    setIsOpen(true);
  };
  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleOpen();
    }
  };
  useOnClickOutside(panelRef as RefObject<HTMLElement>, e => {
    if (isPinned) {
      return;
    }
    if (toggleRef.current?.contains(e.target as Node) === true) {
      return;
    }
    setIsOpen(false);
  });
  useEventListener('keydown', e => {
    if (isOpen && (e as globalThis.KeyboardEvent).key === 'Escape') {
      setIsOpen(false);
    }
  });
  useEffect(() => {
    if (isOpen && position === null) {
      setPosition(getInitialPosition());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  useEventListener('resize', () => {
    if (position === null) {
      return;
    }
    setPosition(getClampedPosition(position));
  });
  const triggerNode = typeof trigger === 'function' ? trigger({
    isOpen,
    isPinned
  }) : trigger;
  return <>
      {trigger != null && <div ref={toggleRef} role="button" tabIndex={0} aria-label={triggerAriaLabel} className={triggerClassName} onClick={handleOpen} onKeyDown={handleTriggerKeyDown}>
          {triggerNode}
        </div>}
      {isOpen && createPortal(<div ref={panelRef} style={{
      position: 'fixed',
      left: position?.x ?? 0,
      top: position?.y ?? 0,
      zIndex,
      width: panelWidth
    }}>
            <Card borderColor={100} shadowSize="md" roundedSize="lg" padding={null} className="!w-full">
              <FloatingCardContextProvider value={{
          handleDragStart,
          isDragging,
          isPinned,
          togglePinned
        }}>
                {children}
              </FloatingCardContextProvider>
            </Card>
          </div>, document.body)}
    </>;
};
FloatingCard.Header = FloatingCardHeader;
FloatingCard.Content = FloatingCardContent;